// 名も無き亡霊「亡霊の波」(shadeRipple) の絵の時刻を当たりへそろえる (2026-10-10・ユーザー採用で既定は on)。
//
// 出所 = docs/research/2026-10-10-shade-fresh-eyes.md の S2。状況と動画 = docs/research/2026-10-10-shade-wave-sync-status.md
//   旧 (2026-10-10 より前の配布版): 床の帯の当たりは帯の経過 1400ms で始まる。亡霊が「放つ」絵 (ripple_cast) に変わるのは亡霊の溜め 1400ms。
//   ところが帯は出た tick に1回ぶん進む (game.ts で spawnZoneHits の直後に updateHits) ので、亡霊より 1 tick (16.7ms) 早い。
//   さらに当たると hitStop で世界が止まり、亡霊は溜めの絵のまま 90〜150ms 止まる。床の噴き上がりは当たりの開始から
//   90ms かけて立ち上がるので、止まったコマでは 0〜0.18 しか出ていない。だから「溜めの絵のまま、床が淡く光るだけで当たる」。
//
// 開発版の切り替え ?shadeWaveSync=0 (または off) で旧へ戻して見比べる (読むのは下の DEV の箱の中だけ)。指定なし／他の値＝既定の on。配布版は常に on。
//   on = 既定 (2026-10-10 ユーザー採用)。絵を当たりの 100ms 前へ前倒しする。
//        ・亡霊は溜めの残り 100ms で「放つ」絵 (ripple_cast) へ切り替える (溜めの膨らみ・震えはそこで解く)。
//        ・床の噴き上がりは当たりの 100ms 前から立ち上げ、当たりの開始 (=被弾のコマ) で立ち上がりきっている。
// 🔴 見た目だけ。当たりの時刻・長さ・範囲・ダメージは変えない (hits.ts・attacks.ts・boss.ts は触らない)。
//   稽古の課題「位置どり」(波から歩いて逃げる) の手応えを変えないため。
//   ⚠️ 識別子に URL のキーの綴りを入れない (scripts/check_dev_leak.mjs が配布ビルドの文字列を見張る)。

import { MIST_CAST_LEAD_MS, shadeMistLookOn } from './shade_wave_mist_fx'

/** 絵を当たりより前へ出す時間 (ms)。噴き上がりの立ち上がり (90ms) より長くして、当たりのコマで全開にする。 */
export const RIPPLE_SYNC_LEAD_MS = 100

/**
 * 亡霊が「放つ」絵へ変わるのは当たりの何 ms 前か。
 *   「床をすべる霧と柱」(既定・DEV の ?shadeWaveLook=now で旧・src/shade_wave_mist_fx.ts) の時だけ 200ms
 *   (柱が当たりの 190ms 前から近い順に立つので、その直前に放つ)。当たりの時刻は変えない。
 */
export function rippleCastLeadMs(mist: boolean = shadeMistLookOn()): number {
  return mist ? MIST_CAST_LEAD_MS : RIPPLE_SYNC_LEAD_MS
}
/** 床の噴き上がりが全開になるまでの時間 (ms)。今の render.ts の rise と同じ。 */
export const RIPPLE_RISE_MS = 90

let on = true
export function rippleSyncOn(): boolean { return on }
/** テスト用。配布版では URL を読まないので、ここを呼ぶ物はテストだけ。 */
export function setRippleSyncForTest(next: boolean): void { on = next }

/**
 * 床の噴き上がり (drawShadeRippleActive) の立ち上がり 0..1。0 = 描かない。
 *   elapsedMs = 帯の経過 (溜めを含む)。off は旧の render.ts と同じ (溜めの間は 0、当たりの開始から 90ms で 1)。
 */
export function rippleBurstRise(elapsedMs: number, telegraphMs: number, m: boolean = on): number {
  const start = m ? telegraphMs - RIPPLE_SYNC_LEAD_MS : telegraphMs
  return Math.max(0, Math.min(1, (elapsedMs - start) / RIPPLE_RISE_MS))
}

interface RippleBossLike {
  state: string
  stateElapsed: number
  currentAttack: { id: string; chargeMs: number } | null
}

/** on の時、溜めの残り 100ms で亡霊を「放つ」絵にするか。 */
export function rippleCastEarly(b: RippleBossLike, m: boolean = on): boolean {
  if (!m || b.state !== 'telegraph' || b.currentAttack?.id !== 'shadeRipple') return false
  return b.stateElapsed * 1000 >= b.currentAttack.chargeMs - rippleCastLeadMs()
}

/**
 * 浮遊の揺れ (shadeBobOffset) へ渡す「いまの絵に入ってからの秒」。
 *   on で前倒しした時だけ、放つ絵へ切り替えた時刻から数える (当たりの開始で揺れが戻ってこないように)。
 */
export function ripplePoseElapsedSec(b: RippleBossLike, m: boolean = on): number {
  if (!m || b.currentAttack?.id !== 'shadeRipple') return b.stateElapsed
  if (rippleCastEarly(b, m)) return b.stateElapsed - (b.currentAttack.chargeMs - rippleCastLeadMs()) / 1000
  if (b.state === 'active') return b.stateElapsed + rippleCastLeadMs() / 1000
  return b.stateElapsed
}

// URL の読み取りは最後 (上の let が初期化された後)。配布版はこの箱ごと消える = 常に on。
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const v = new URLSearchParams(window.location.search).get('shadeWaveSync')
  on = !(v === '0' || v === 'off')
}
